import { useState, useEffect, useRef } from "react";
import useStatementActions from "../hooks/useStatementActions";
import { Offcanvas, Row, Col, Nav, Button, Form } from "react-bootstrap";
import useFetchData from "./useFetchDatav2";
import { useToast } from "../Context/useToast";
import ProgressBar from "./ProgressBar";
import prettyBytes from "pretty-bytes";
import { format, parseISO } from "date-fns";
import CurrencyFormatter from "./CurrencyFormat";
import { FaBalanceScale, FaCloudDownloadAlt, FaTrash } from "react-icons/fa";
import { FaEquals, FaNotEqual, FaMinus, FaRegCircle, FaFlag, FaRegFlag } from "react-icons/fa";
import "../apps/InvoiceStatements.css";
import ModalDialogue from "./ModalDialogue";
import PdfModal from "./PdfModal";
import PayeePickerModal from "./PayeePickerModal";


const InvoiceStatementModal = ({ invStatId, dpaId, invStatName, invStatDate, parentActiveSection, onDeleted }) => {
   const getLabelById = (id, array) => {
      const item = array.find((elem) => elem.value == id);
      return item ? item.label : "";
   };

   const [show, setShow] = useState(true);
   const [formIsDirty, setFormIsDirty] = useState(false);
   const isExitingRef = useRef(false);
   const [activeSection, setActiveSection] = useState("summary");

   const [editMode, setEditMode] = useState(null);
   const [payloadInvSt, setPayloadInvSt] = useState(null);

   const [isLoadingInvSt, setIsLoadingInvSt] = useState(true);

   const [showTermsEditor, setShowTermsEditor] = useState(false);
   const [selectedTerm, setSelectedTerm] = useState("");
   const [isSavingTerms, setIsSavingTerms] = useState(false);
   const [termsSaveTarget, setTermsSaveTarget] = useState("supplier"); // "supplier" | "statement"
   const [termsRefreshTick, setTermsRefreshTick] = useState(0);

   const [showPayeePicker, setShowPayeePicker] = useState(false);
   const [pendingPayee, setPendingPayee] = useState(null); // picker selection awaiting confirm
   const [isRelinking, setIsRelinking] = useState(false);

   const [showDateEditor, setShowDateEditor] = useState(false);
   const [statementDateInput, setStatementDateInput] = useState("");
   const [isSavingDate, setIsSavingDate] = useState(false);
   const [showStatementPdfModal, setShowStatementPdfModal] = useState(false);
   const [statementImageData, setStatementImageData] = useState(null);
   const { triggerToast } = useToast();
   const { fetchData } = useFetchData();
   const documentGrade = payloadInvSt?.dpa_confidence_breakdown?.grade || payloadInvSt?.grade || "-";

   const {
      goReconcile, goDownload, requestDelete, isDeleting,
      dialogState, onDialogResponse,
   } = useStatementActions(
      {
         invStatId,
         dpaId,
         docId: payloadInvSt?.doc_id,
         supplierName: invStatName ?? payloadInvSt?.sm_name,
         statementDate: invStatDate ?? payloadInvSt?.sup_state_frmt_statement_date,
         grade: payloadInvSt?.dpa_confidence_breakdown?.grade || payloadInvSt?.grade || "-",
         activeSection: parentActiveSection,
         displayName: payloadInvSt?.doc_orig_name,
      },
      { onDeleted }
   );

   // Fetch Document
   useEffect(() => {
      const getInvoiceStatement = async () => {
         if (!dpaId) {
            setIsLoadingInvSt(false);
            setPayloadInvSt(null);
            return;
         }
         setIsLoadingInvSt(true);
         const url = "/getInvStatementSummary.sz";
         const method = "POST";
         const formData = new FormData();
         formData.append("_dpa_id", dpaId);

         try {
            const { responseData } = await fetchData(url, method, formData, "form-data");
            console.log("RESPONSEDATA:", responseData);
            setIsLoadingInvSt(false);
            if (responseData.st === 1) {
               if (responseData?.payload?.length > 0) {
                  const statementData = responseData?.payload[0].statement;
                  setPayloadInvSt(statementData);
               } else {
                  setPayloadInvSt(null);
               }
            } else {
               triggerToast("warning", responseData.m_en || "Issue retrieving data", false, "", false);
            }
         } catch (error) {
            setIsLoadingInvSt(false);
            triggerToast("error", "2 We seem to have an issue accessing your data, please try again later.", true, "Network Issue", false, 0);
         }
      };
      getInvoiceStatement();
   }, [dpaId, termsRefreshTick]);

   const formatDate = (dateString) => {
      try {
         const cleanedDateString = dateString.split(".")[0]; // Remove microseconds
         const date = parseISO(cleanedDateString);
         return format(date, "MMM dd, yyyy hh:mm a");
      } catch (error) {
         console.error("Invalid date:", error);
         return "";
      }
   };

   useEffect(() => {
      setShowStatementPdfModal(false);
      setStatementImageData(null);
   }, [dpaId, payloadInvSt?.doc_id]);

   const convertBlobToBase64 = (blob) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.readAsDataURL(blob);
         reader.onloadend = () => resolve(reader.result.split(",")[1]);
         reader.onerror = reject;
      });
   };

   const fetchStatementPdf = async () => {
      if (!invStatId) return null;

      const url = "/getfile.sz";
      const method = "POST";
      const formData = new FormData();
      formData.append("_inv_stat_id", invStatId);

      const { responseData, fetchError } = await fetchData(url, method, formData, "form-data", "blob");

      if (fetchError || !responseData) {
         throw new Error("Failed to retrieve statement PDF.");
      }

      return convertBlobToBase64(responseData);
   };

   const handleStatementView = async () => {
      if (!invStatId || !payloadInvSt?.doc_id) return;

      try {
         const base64Data = await fetchStatementPdf();
         if (!base64Data) return;
         setStatementImageData(base64Data);
         setShowStatementPdfModal(true);
      } catch (error) {
         triggerToast("error", "Unable to open the statement right now. Please try again.", true, "Issue", false, 0);
      }
   };

   const formatStatementDate = (dateString) => {
      if (!dateString) return null;
      try {
         const parsed = parseISO(dateString.split(".")[0]);
         return isNaN(parsed) ? null : format(parsed, "MMM dd, yyyy");
      } catch {
         return null;
      }
   };

   const handleOpenDateEditor = () => {
      const existing = payloadInvSt?.sup_state_frmt_statement_date;
      if (existing) {
         try {
            const parsed = parseISO(existing.split(".")[0]);
            setStatementDateInput(isNaN(parsed) ? "" : format(parsed, "yyyy-MM-dd"));
         } catch {
            setStatementDateInput("");
         }
      } else {
         setStatementDateInput("");
      }
      setShowDateEditor(true);
   };

   const handleSaveStatementDate = async () => {
      if (!statementDateInput || !payloadInvSt?.sup_state_id) return;
      setIsSavingDate(true);

      const url = "/updateStatementDate.sz";
      const method = "POST";
      const formData = new FormData();
      formData.append("_id", payloadInvSt?.sup_state_id);
      formData.append("_date", statementDateInput);

      try {
         const { responseData } = await fetchData(url, method, formData, "form-data");
         if (responseData?.st === 1) {
            setPayloadInvSt((prev) => ({ ...prev, sup_state_frmt_statement_date: statementDateInput }));
            setShowDateEditor(false);
            triggerToast("success", "Statement date updated.", false, "", false);
         } else {
            triggerToast("error", responseData?.m_en || "Failed to update statement date.", true, "Error", false, 0);
         }
      } catch {
         triggerToast("error", "Network issue, please try again.", true, "Network Issue", false, 0);
      } finally {
         setIsSavingDate(false);
      }
   };

   const handleSaveTerms = async () => {
      // selectedTerm is one of "Net 10" | "Net 15" | "Net 20" | "Net 30" | "Net 45" | "Net 60" | "Net 90"
      const netDays = parseInt(selectedTerm.replace(/\D/g, ""), 10);
      if (Number.isNaN(netDays)) return;
      setIsSavingTerms(true);

      const isSupplier = termsSaveTarget === "supplier";
      const url = isSupplier ? "/updateSupplierTerms.sz" : "/updateStatementTerms.sz";
      const formData = new FormData();
      if (isSupplier) {
         formData.append("_sm_ext_id", payloadInvSt?.sm_ext_id);
      } else {
         formData.append("_ss_ext_id", payloadInvSt?.sup_state_id); // external uuid
      }
      formData.append("_net_days", String(netDays));

      try {
         const { responseData } = await fetchData(url, "POST", formData, "form-data");
         if (responseData?.st === 1) {
            setShowTermsEditor(false);
            setSelectedTerm("");
            triggerToast("success", "Terms saved.", false, "", false);
            setTermsRefreshTick((t) => t + 1); // refetch resolved effective terms + due date
         } else if (responseData?.st === -2 || responseData?.st === 0) {
            triggerToast("warning", "Your session has expired, please sign in.", true, "Session", false, 0);
         } else {
            triggerToast("error", responseData?.mg || "Failed to save terms.", true, "Error", false, 0);
         }
      } catch {
         triggerToast("error", "Network issue, please try again.", true, "Network Issue", false, 0);
      } finally {
         setIsSavingTerms(false);
      }
   };

   const handleRelinkPayee = async (target) => {
      if (!target?.id || !payloadInvSt?.payee_alias_id) return;
      setIsRelinking(true);
      try {
         const formData = new FormData();
         formData.append("_alias_id", payloadInvSt.payee_alias_id);
         formData.append("_master_id", target.id);
         formData.append("_statement_ext_id", payloadInvSt.sup_state_id || "");
         formData.append("_surface", "offcanvas");
         const { responseData } = await fetchData("/relinksupplieralias.sz", "POST", formData, "form-data");
         const result = Array.isArray(responseData?.payload) ? responseData.payload[0] : null;
         if (responseData?.st === 1 && result?.status !== "error") {
            triggerToast("success",
               `Payee updated. Future statements named "${payloadInvSt.payee_alias_name || payloadInvSt.sm_name}" will resolve to ${target.name}.`,
               false, "Payee changed", false);
            setPendingPayee(null);
            setShowPayeePicker(false);
            setTermsRefreshTick((t) => t + 1); // re-fetch the summary
         } else {
            triggerToast("warning", result?.reason || responseData?.mg || "Could not change the payee.", false, "", false);
         }
      } catch {
         triggerToast("error", "We seem to have an issue accessing your data, please try again later.", true, "Network Issue", false, 0);
      } finally {
         setIsRelinking(false);
      }
   };

   return (
      <>
         <Offcanvas style={{ top: "71px", zIndex: "799" }} show={show} scroll={true} backdrop={false} placement="end" className="offcanvas-width-invoice-statements">
            <Offcanvas.Header
               className="d-flex flex-column align-items-start w-100"
               style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1055,
               }}>
               <Row className="d-flex align-items-start flex-nowrap w-100">
                  <Col xs="auto" className="text-start">
                     {isLoadingInvSt ? (
                        <div className="placeholder-glow">
                           <span className="placeholder" style={{ width: "77px", height: "100px" }}></span>
                        </div>
                     ) : payloadInvSt === null ? (
                        <span></span>
                     ) : (
                        <div className="position-relative d-inline-block">
                           <button className="offcanvas-statement-preview offcanvas-statement-preview-selected border border-primary shadow-sm" onClick={handleStatementView} type="button" aria-label="View statement">
                              <img src={`https://storage.googleapis.com/0b4be1ac-452a-4db9-a8cc-a38082af5a60/${payloadInvSt.doc_id}.png`} className="img-fluid offcanvas-statement-preview-image" loading="lazy" alt="Document" />
                              <span className="offcanvas-statement-preview-view text-primary position-absolute top-50 start-50 translate-middle">
                                 View
                              </span>
                           </button>
                        </div>
                     )}
                  </Col>
                  <Col style={{ minWidth: 0 }}>
                     {isLoadingInvSt ? (
                        <div className="fs-5 mb-3 ms-2">
                           <div className="placeholder-glow">
                              <span className="placeholder" style={{ width: "100%", height: "25px" }}></span>
                           </div>
                        </div>
                     ) : (
                        <div
                           className="fs-5 mb-3 ms-2 position-relative"
                           style={{
                              maxWidth: "300px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                           }}>
                           {payloadInvSt?.doc_orig_name || "Document not Found"}
                        </div>
                     )}
                     <div className="d-flex gap-2">
                        {isLoadingInvSt ? (
                           <span style={{ width: "100%", height: "25px" }}> </span>
                        ) : (
                           <>
                              <button
                                 className="btn btn-link d-inline-flex align-items-center justify-content-center pt-1 pb-1 text-primary rounded-pill btn-link-offcanvas-border"
                                 onClick={goReconcile}
                                 disabled={!invStatId || isDeleting}
                                 aria-disabled={!invStatId || isDeleting}
                                 title={!invStatId ? "Statement not created yet" : "Reconcile"}
                              >
                                 <FaBalanceScale size={16} className="me-2" />
                                 Reconcile
                              </button>
                              <button
                                 className="btn btn-link d-inline-flex align-items-center justify-content-center pt-1 pb-1 text-primary rounded-pill btn-link-offcanvas-border"
                                 disabled={!invStatId || isDeleting}
                                 aria-disabled={!invStatId || isDeleting}
                                 onClick={goDownload}
                                 title={!invStatId ? "Statement not created yet" : "Download"}
                              >
                                 <FaCloudDownloadAlt size={16} className="me-2" />
                                 Download
                              </button>
                              <button
                                 onClick={requestDelete}
                                 className="btn btn-link d-inline-flex align-items-center justify-content-center pt-1 pb-1 text-primary rounded-pill btn-link-offcanvas-border"
                                 disabled={!invStatId || isDeleting}
                                 aria-disabled={!invStatId || isDeleting}
                                 title={!invStatId ? "Statement not created yet" : isDeleting ? "Deleting..." : "Delete"}
                              >
                                 <FaTrash size={16} className="me-2" />
                                 {isDeleting ? (
                                    <>
                                       Deleting
                                       <span className="bc-bouncing-dots ms-1" aria-hidden="true">
                                          <span></span>
                                          <span></span>
                                          <span></span>
                                       </span>
                                    </>
                                 ) : (
                                    "Delete"
                                 )}
                              </button>
                           </>
                        )}
                     </div>
                  </Col>
               </Row>
            </Offcanvas.Header>

            <Offcanvas.Body className="pt-1" style={{ maxHeight: "calc(100vh - 3.5rem)", overflowY: "auto" }}>
               <Nav className="nav-line mb-2">
                  <Nav.Link eventKey="summary" active={activeSection === "summary"} onClick={() => setActiveSection("summary")}>
                     Summary
                  </Nav.Link>
                  <Nav.Link eventKey="document" active={activeSection === "document"} onClick={() => setActiveSection("document")}>
                     Document
                  </Nav.Link>
               </Nav>

               {activeSection === "summary" && (
                  <div id="summary-section" style={{ paddingTop: "10px" }}>
                     <div className="card-bc border-0 mb-3">
                        <div className="card-body border-top border-1" style={{ "--card-body-content": "'Supplier Statement Summary'" }}>
                           {/* Statement Date */}
                           <Row className="mt-3 mb-3">
                              <Col>
                                 {isLoadingInvSt ? (
                                    <div className="placeholder-glow">
                                       <span className="placeholder d-block" style={{ width: "60%", height: "20px" }}></span>
                                    </div>
                                 ) : showDateEditor ? (
                                    <div>
                                       <div className="fw-semibold mb-1">Statement Date</div>
                                       <div className="d-flex align-items-center gap-2">
                                          <Form.Control
                                             type="date"
                                             size="sm"
                                             style={{ maxWidth: "160px" }}
                                             value={statementDateInput}
                                             onChange={(e) => setStatementDateInput(e.target.value)}
                                          />
                                          <Button size="sm" variant="primary" onClick={handleSaveStatementDate} disabled={!statementDateInput || isSavingDate}>
                                             {isSavingDate ? "Saving…" : "Save"}
                                          </Button>
                                          <Button size="sm" variant="outline-secondary" onClick={() => setShowDateEditor(false)} disabled={isSavingDate}>
                                             Cancel
                                          </Button>
                                       </div>
                                    </div>
                                 ) : formatStatementDate(payloadInvSt?.sup_state_frmt_statement_date) ? (
                                    <div className="d-flex align-items-center gap-2">
                                       <div>
                                          <span className="fw-semibold me-2">Statement Date</span>
                                          <span
                                             className="fw-semibold text-primary"
                                             style={{ cursor: "pointer", textDecoration: "underline" }}
                                             onClick={handleOpenDateEditor}
                                          >
                                             {formatStatementDate(payloadInvSt.sup_state_frmt_statement_date)}
                                          </span>
                                       </div>
                                    </div>
                                 ) : (
                                    <div
                                       className="fw-semibold text-primary"
                                       style={{ cursor: "pointer", textDecoration: "underline" }}
                                       onClick={handleOpenDateEditor}
                                    >
                                       Add Statement Date
                                    </div>
                                 )}
                              </Col>
                           </Row>
                           <hr className="mt-0 mb-3" />
                           <Row className="d-flex align-items-center">
                              <Col>
                                 {isLoadingInvSt ? (
                                    <div className="placeholder-glow">
                                       <span className="placeholder d-block" style={{ width: "100%", height: "40px" }}></span>
                                    </div>
                                 ) : (
                                    <>
                                       <div className="d-flex justify-content-between text-center pb-1">
                                          <div className="d-flex flex-column align-items-center" style={{ minWidth: "120px" }}>
                                             <div className="fw-semibold text-nowrap">Supplier Net Total</div>
                                          </div>
                                          <div className="d-flex flex-column align-items-center" style={{ minWidth: "40px" }}>
                                             <div className="fw-bold"></div>
                                          </div>
                                          <div className="d-flex flex-column align-items-center" style={{ minWidth: "120px" }}>
                                             <div className="fw-semibold text-nowrap">Total Net Entries</div>
                                          </div>
                                          <div className="d-flex flex-column align-items-center" style={{ minWidth: "40px" }}>
                                             <div className="fw-bold"></div>
                                          </div>
                                          <div className="d-flex flex-column align-items-center" style={{ minWidth: "120px" }}>
                                             <div className="fw-semibold text-nowrap">Difference</div>
                                          </div>
                                       </div>
                                       <div className="d-flex justify-content-between text-center mt-1">
                                          <div className="d-flex flex-column align-items-center" style={{ minWidth: "120px" }}>
                                             <span className="fs-5">
                                                {payloadInvSt?.invoice_total_amount != null &&
                                                new Intl.NumberFormat("en-US", {
                                                   style: "currency",
                                                   currency: "USD",
                                                }).format(payloadInvSt.invoice_total_amount)}
                                             </span>
                                          </div>
                                          <div className="d-flex flex-column align-items-center justify-content-center" style={{ minWidth: "40px" }}>
                                             <FaMinus color="black" />
                                          </div>
                                          <div className="d-flex flex-column align-items-center" style={{ minWidth: "120px" }}>
                                             <span className="fs-5">
                                                {payloadInvSt?.invoice_state_reconciled &&
                                                   new Intl.NumberFormat("en-US", {
                                                      style: "currency",
                                                      currency: "USD",
                                                   }).format(payloadInvSt.invoice_state_reconciled)}
                                             </span>
                                          </div>
                                          <div className="d-flex flex-column align-items-center justify-content-center" style={{ minWidth: "40px" }}>
                                             <span>{((payloadInvSt?.invoice_total_amount ?? 0) - (payloadInvSt?.invoice_state_reconciled ?? 0)) === 0 ? <FaEquals color="green" /> : <FaNotEqual color="orange" />}</span>
                                          </div>
                                          <div className="d-flex flex-column align-items-center" style={{ minWidth: "120px" }}>
                                             <span className="fs-5">
                                                {payloadInvSt?.invoice_total_amount != null &&
                                                   payloadInvSt?.invoice_state_reconciled != null &&
                                                   new Intl.NumberFormat("en-US", {
                                                      style: "currency",
                                                      currency: "USD",
                                                   }).format((payloadInvSt.invoice_total_amount ?? 0) - (payloadInvSt?.invoice_state_reconciled ?? 0))}
                                             </span>
                                          </div>
                                       </div>
                                    </>
                                 )}
                              </Col>
                           </Row>
                           <Row className="d-flex mt-3 align-items-start text-center">
                              <Col xs={4} className="d-flex flex-column align-items-center">
                                 <div className="fw-semibold mb-2">Document Grade</div>
                                 {isLoadingInvSt ? (
                                    <div className="placeholder-glow">
                                       <span className="placeholder d-block" style={{ width: "20px", height: "40px" }}></span>
                                    </div>
                                 ) : (
                                    <div>
                                       <span className="fs-1">{documentGrade}</span>
                                    </div>
                                 )}
                              </Col>
                              <Col xs={4} className="d-flex flex-column align-items-center">
                                 <div className="fw-semibold mb-2">Invoices Matched</div>
                                 {isLoadingInvSt ? (
                                    <div className="placeholder-glow">
                                       <span className="placeholder d-block" style={{ width: "50px", height: "40px" }}></span>
                                    </div>
                                 ) : (
                                    <p className="mb-0 fs-4">
                                       <span className="fs-1">{payloadInvSt?.invoice_state_match}</span> / {payloadInvSt?.invoice_line_count}
                                    </p>
                                 )}
                                 <ProgressBar completion={isLoadingInvSt || !payloadInvSt ? 0 : Math.round(((payloadInvSt.invoice_state_match ?? 0) / (payloadInvSt.invoice_line_count ?? 1)) * 100)} isLoading={isLoadingInvSt} />
                              </Col>
                              <Col xs={4} className="d-flex flex-column align-items-center">
                                 <div className="fw-semibold mb-2">Overrides</div>
                                 {isLoadingInvSt ? (
                                    <div className="placeholder-glow">
                                       <span className="placeholder d-block" style={{ width: "20px", height: "40px" }}></span>
                                    </div>
                                 ) : (
                                    <p className="fs-4 mb-0">
                                       <span className="fs-1">{payloadInvSt?.invoice_state_override}</span>
                                    </p>
                                 )}
                              </Col>
                           </Row>

                           <Row className="mt-3 d-flex align-items-center">
                              <div className="d-flex justify-content-between text-center pb-1">
                                 <div className="d-flex flex-column align-items-center" style={{ minWidth: "120px" }}>
                                    <div className="fw-semibold text-nowrap">Total Not Reconciled Count</div>
                                 </div>
                                 <div className="d-flex flex-column align-items-center" style={{ minWidth: "120px" }}>
                                    <div className="fw-semibold">Total Not Reconciled Amount</div>
                                 </div>
                              </div>

                              <div className="d-flex justify-content-between text-center mt-2">
                                 <div className="d-flex flex-column align-items-center" style={{ minWidth: "120px" }}>
                                    <span className="fs-5">{payloadInvSt?.invoice_state_not_reconciled_count && payloadInvSt?.invoice_state_not_reconciled_count}</span>
                                 </div>
                                 <div className="d-flex flex-column align-items-center" style={{ minWidth: "120px" }}>
                                    <span className="fs-5">
                                       {payloadInvSt?.invoice_state_not_reconciled &&
                                          new Intl.NumberFormat("en-US", {
                                             style: "currency",
                                             currency: "USD",
                                          }).format(payloadInvSt.invoice_state_not_reconciled)}
                                    </span>
                                 </div>
                              </div>
                           </Row>
                        </div>
                     </div>
                     <div className="card-bc border-0 mb-2">
                        <div className="card-body border-top border-1" style={{ "--card-body-content": "'Follow Ups'" }}>
                           <Row className="mt-2">
                              <Col className="d-flex flex-column align-items-center">
                                 <div className="fw-semibold mb-2">Oustanding Follow-ups</div>
                                 {isLoadingInvSt ? (
                                    <div className="placeholder-glow">
                                       <span className="placeholder d-block" style={{ width: "20px", height: "40px" }}></span>
                                    </div>
                                 ) : (
                                    <p className="fs-5 mb-0">
                                       <span className="fs-1">{payloadInvSt?.active_follow_ups}</span>
                                    </p>
                                 )}
                              </Col>
                           </Row>
                        </div>
                     </div>
                     <div className="card-bc border-0 mb-3">
                        <div className="card-body border-top border-1" style={{ "--card-body-content": "'Supplier'" }}>
                           <Row className="mt-3">
                              <Col>
                                 <div className="d-flex flex-column flex-fill">
                                    <div className="mt-1 fw-semibold text-nowrap">Supplier</div>
                                    <div>
                                       {isLoadingInvSt ? (
                                          <div className="placeholder-glow">
                                             <span className="placeholder" style={{ width: "100%", height: "20px" }}></span>
                                          </div>
                                       ) : (
                                          <div className="d-flex align-items-center gap-2">
                                             <span>{payloadInvSt?.sm_name}</span>
                                             {payloadInvSt?.payee_alias_id ? (
                                                <Button
                                                   variant="outline-secondary"
                                                   size="sm"
                                                   className="py-0 px-2"
                                                   onClick={() => setShowPayeePicker(true)}
                                                >
                                                   Change
                                                </Button>
                                             ) : (
                                                <span
                                                   className="text-secondary small"
                                                   title="No alias recorded for this statement (processed before the identity upgrade). Republish the document to record one."
                                                >
                                                   —
                                                </span>
                                             )}
                                          </div>
                                       )}
                                    </div>
                                    {payloadInvSt?.payee_alias_name && !isLoadingInvSt && (
                                       <div className="text-secondary" style={{ fontSize: "0.75rem" }}>
                                          statement name: "{payloadInvSt.payee_alias_name}"
                                       </div>
                                    )}
                                 </div>
                              </Col>
                           </Row>
                           <Row className="mt-3">
                              <Col md={6}>
                                 <div className="d-flex flex-column flex-fill">
                                    <div className="mt-1 fw-semibold text-nowrap">Customer</div>
                                    <div>
                                       {isLoadingInvSt ? (
                                          <div className="placeholder-glow">
                                             <span className="placeholder" style={{ width: "100%", height: "20px" }}></span>
                                          </div>
                                       ) : (
                                          payloadInvSt?.sup_state_customer_name
                                       )}
                                    </div>
                                 </div>
                              </Col>
                              <Col md={6}>
                                 <div className="mt-1 fw-semibold text-nowrap">Customer Account</div>
                                 <div>
                                    {isLoadingInvSt ? (
                                       <div className="placeholder-glow">
                                          <span className="placeholder" style={{ width: "100%", height: "20px" }}></span>
                                       </div>
                                    ) : (
                                       payloadInvSt?.sup_state_account_number
                                    )}
                                 </div>
                              </Col>
                           </Row>
                           <Row className="mt-3">
                              <Col>
                                 {isLoadingInvSt ? (
                                    <>
                                       <div className="mt-1 fw-semibold text-nowrap">Terms</div>
                                       <div className="placeholder-glow">
                                          <span className="placeholder" style={{ width: "100%", height: "20px" }}></span>
                                       </div>
                                    </>
                                 ) : payloadInvSt?.terms_due_date && !showTermsEditor ? (
                                    <>
                                       <div className="mt-1 fw-semibold text-nowrap">Terms</div>
                                       <div>{payloadInvSt.effective_label || payloadInvSt.sup_state_terms}</div>
                                       <div className="text-secondary" style={{ fontSize: "0.85rem" }}>
                                          Due {formatStatementDate(payloadInvSt.terms_due_date)}
                                       </div>
                                       <div
                                          className="text-primary mt-1"
                                          style={{ cursor: "pointer", fontSize: "0.85rem", textDecoration: "underline" }}
                                          onClick={() => setShowTermsEditor(true)}
                                       >
                                          Edit terms
                                       </div>
                                    </>
                                 ) : payloadInvSt?.sup_state_terms && !showTermsEditor ? (
                                    <>
                                       <div className="mt-1 fw-semibold text-nowrap">Terms</div>
                                       <div>{payloadInvSt.sup_state_terms}</div>
                                       {payloadInvSt?.sup_state_frmt_statement_date ? (
                                          <div
                                             className="text-primary mt-1"
                                             style={{ cursor: "pointer", fontSize: "0.85rem", textDecoration: "underline" }}
                                             onClick={() => setShowTermsEditor(true)}
                                          >
                                             Set terms
                                          </div>
                                       ) : (
                                          <div className="mt-1 text-secondary" style={{ fontSize: "0.85rem" }}>
                                             Add a statement date to set terms
                                          </div>
                                       )}
                                    </>
                                 ) : !showTermsEditor ? (
                                    payloadInvSt?.sup_state_frmt_statement_date ? (
                                       <div
                                          className="mt-1 fw-semibold text-primary"
                                          style={{ cursor: "pointer", textDecoration: "underline" }}
                                          onClick={() => setShowTermsEditor(true)}
                                       >
                                          Add Terms
                                       </div>
                                    ) : (
                                       <div className="mt-1 text-secondary" style={{ fontSize: "0.85rem" }}>
                                          Add a statement date to set terms
                                       </div>
                                    )
                                 ) : (
                                    <div className="mt-1">
                                       <div className="fw-semibold mb-2">Terms</div>
                                       <Form.Select
                                          size="sm"
                                          value={selectedTerm}
                                          onChange={(e) => setSelectedTerm(e.target.value)}
                                          className="mb-2"
                                       >
                                          <option value="">Select terms...</option>
                                          <option value="Net 10">Net 10</option>
                                          <option value="Net 15">Net 15</option>
                                          <option value="Net 20">Net 20</option>
                                          <option value="Net 30">Net 30</option>
                                          <option value="Net 45">Net 45</option>
                                          <option value="Net 60">Net 60</option>
                                          <option value="Net 90">Net 90</option>
                                       </Form.Select>

                                       {payloadInvSt?.sup_state_terms && (
                                          <div className="text-secondary mb-2" style={{ fontSize: "0.8rem" }}>
                                             Extracted text: {payloadInvSt.sup_state_terms}
                                          </div>
                                       )}

                                       <Form.Check
                                          type="radio"
                                          id="terms-target-supplier"
                                          name="termsTarget"
                                          label={`Set as default for ${payloadInvSt?.sm_name || "this supplier"}`}
                                          checked={termsSaveTarget === "supplier"}
                                          onChange={() => setTermsSaveTarget("supplier")}
                                          className="mb-1"
                                       />
                                       <Form.Check
                                          type="radio"
                                          id="terms-target-statement"
                                          name="termsTarget"
                                          label="Apply to this statement only"
                                          checked={termsSaveTarget === "statement"}
                                          onChange={() => setTermsSaveTarget("statement")}
                                          className="mb-2"
                                       />

                                       <div className="d-flex gap-2">
                                          <Button
                                             size="sm"
                                             variant="primary"
                                             onClick={handleSaveTerms}
                                             disabled={!selectedTerm || isSavingTerms}
                                          >
                                             {isSavingTerms ? "Saving..." : "Save"}
                                          </Button>
                                          <Button
                                             size="sm"
                                             variant="outline-secondary"
                                             onClick={() => {
                                                setShowTermsEditor(false);
                                                setSelectedTerm("");
                                             }}
                                          >
                                             Cancel
                                          </Button>
                                       </div>
                                    </div>
                                 )}
                              </Col>
                           </Row>
                        </div>
                     </div>
                  </div>
               )}
               {activeSection === "document" && (
                  <div id="document-section" style={{ paddingTop: "10px" }}>
                     <div className="card-bc border-0">
                        <div className="card-body border-top border-1" style={{ "--card-body-content": "'Document'" }}>
                           <Row className="mt-3">
                              <Col md={6}>
                                 <div className="mt-1 fw-semibold text-nowrap">Pages</div>
                                 <div>
                                    {isLoadingInvSt ? (
                                       <div className="placeholder-glow">
                                          <span className="placeholder" style={{ width: "40px", height: "20px" }}></span>
                                       </div>
                                    ) : (
                                       payloadInvSt?.sup_state_noofpages
                                    )}
                                 </div>
                                 <div className="mt-1 fw-semibold text-nowrap">File Size</div>
                                 <div>
                                    {isLoadingInvSt ? (
                                       <div className="placeholder-glow">
                                          <span className="placeholder" style={{ width: "80px", height: "20px" }}></span>
                                       </div>
                                    ) : payloadInvSt?.doc_size ? (
                                       prettyBytes(payloadInvSt?.doc_size)
                                    ) : (
                                       "Unknown Size"
                                    )}
                                 </div>
                                 <div className="mt-1 fw-semibold text-nowrap">Uploaded On</div>
                                 <div>
                                    {isLoadingInvSt ? (
                                       <div className="placeholder-glow">
                                          <span className="placeholder" style={{ width: "100%", height: "20px" }}></span>
                                       </div>
                                    ) : (
                                       <div className="text-start text-truncate">{formatDate(payloadInvSt?.doc_upload_on)}</div>
                                    )}
                                 </div>
                              </Col>
                              <Col md={6}>
                                 <div className="mt-1 fw-semibold text-nowrap">File Name</div>
                                 <div>
                                    {isLoadingInvSt ? (
                                       <div className="placeholder-glow">
                                          <span className="placeholder" style={{ width: "100%", height: "20px" }}></span>
                                       </div>
                                    ) : (
                                       <div className="text-start text-truncate">{payloadInvSt?.doc_orig_name}</div>
                                    )}
                                 </div>
                                 <div className="mt-1 fw-semibold text-nowrap">File Type</div>
                                 <div>
                                    {isLoadingInvSt ? (
                                       <div className="placeholder-glow">
                                          <span className="placeholder" style={{ width: "100%", height: "20px" }}></span>
                                       </div>
                                    ) : (
                                       <div className="text-start text-truncate">{payloadInvSt?.doc_file_type}</div>
                                    )}
                                 </div>
                                 <div className="mt-1 fw-semibold text-nowrap">Uploaded By</div>
                                 <div>
                                    {isLoadingInvSt ? (
                                       <div className="placeholder-glow">
                                          <span className="placeholder" style={{ width: "100%", height: "20px" }}></span>
                                       </div>
                                    ) : (
                                       <div className="text-start text-truncate">{payloadInvSt?.doc_upload_by}</div>
                                    )}
                                 </div>
                              </Col>
                           </Row>
                        </div>
                     </div>
                  </div>
               )}
            </Offcanvas.Body>

            <div className="border-top p-3 position-sticky bottom-0 w-100" style={{ zIndex: 1055 }}>
               <Row>
                  <Col className="d-flex justify-content-between align-items-center">{/* Additional footer content if needed */}</Col>
               </Row>
            </div>
         </Offcanvas>
         <ModalDialogue isModalDialogueOpen={dialogState.isModalDialogueOpen} question={dialogState.question} onModalDialogueResponse={onDialogResponse} />
         {showStatementPdfModal && (
            <PdfModal
               imageData={statementImageData}
               propScale={1.0}
               docObj={{
                  name: payloadInvSt?.doc_orig_name,
                  uploadOn: payloadInvSt?.doc_upload_on,
                  uploadBy: payloadInvSt?.doc_upload_by,
               }}
               onCloseModal={() => setShowStatementPdfModal(false)}
            />
         )}
         <PayeePickerModal
            show={showPayeePicker && !pendingPayee}
            onClose={() => setShowPayeePicker(false)}
            onSelect={(record) => setPendingPayee(record)}
            title="Change payee for this statement"
            initialQuery={payloadInvSt?.payee_alias_name || payloadInvSt?.sm_name || ""}
            excludeMasterId={payloadInvSt?.payee_master_id}
         />
         {pendingPayee && (
            <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.4)", zIndex: 1070 }}>
               <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                     <div className="modal-body">
                        <p className="fw-semibold mb-1">Change payee?</p>
                        <p className="small text-secondary mb-0">
                           {`Future statements named "${payloadInvSt?.payee_alias_name || payloadInvSt?.sm_name}" will resolve to ${pendingPayee?.name}. This statement and this name's ERP history move with it.`}
                        </p>
                     </div>
                     <div className="modal-footer py-2">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setPendingPayee(null)} disabled={isRelinking}>Cancel</button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => handleRelinkPayee(pendingPayee)} disabled={isRelinking}>
                           {isRelinking ? "Changing…" : "Change payee"}
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         )}
      </>
   );
};

export default InvoiceStatementModal;
